import { useEffect, useRef } from 'react'
import { Chart, registerables } from 'chart.js'
import type { ReportChart as ChartData } from '../../../api/playerReports'
import { baseChartOptions } from '../../../components/chartTheme'
import { palette } from '@versports/theme'

Chart.register(...registerables)
const colors = [palette.primary, palette.secondary, palette.textPrimary]

export function ReportChart({ chart }: { chart: ChartData }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const hasValues = chart.series.some((s) => s.values.some((v) => v !== null))
  useEffect(() => {
    if (!canvas.current || !hasValues) return
    const instance = new Chart(canvas.current, {
      type: chart.kind,
      data: {
        labels: chart.labels,
        datasets: chart.series.map((s, i) => ({
          label: s.label,
          data: s.values,
          borderColor: colors[i % colors.length],
          backgroundColor: colors[i % colors.length],
          borderWidth: 2,
          pointRadius: 3,
          borderDash: i ? [5, 4] : [],
          tension: 0,
          spanGaps: false,
        })),
      },
      options: {
        ...baseChartOptions(),
        animation: false,
        maintainAspectRatio: false,
        plugins: {
          ...baseChartOptions().plugins,
          legend: {
            position: 'bottom',
            labels: { color: palette.textSecondary, boxWidth: 12 },
          },
        },
        scales: {
          y: {
            beginAtZero:
              chart.minimum === undefined && !chart.id.startsWith('hr'),
            min: chart.minimum,
            max: chart.maximum ?? undefined,
            title: {
              display: true,
              text: chart.unit,
              color: palette.textSecondary,
            },
            ticks: {
              color: palette.textSecondary,
              stepSize:
                chart.minimum === 1 && chart.maximum === 4 ? 1 : undefined,
              precision: ['jugadas', 'respuestas'].includes(chart.unit)
                ? 0
                : undefined,
            },
            grid: { color: palette.outline },
          },
          x: {
            ticks: {
              color: palette.textSecondary,
              maxRotation: 45,
              autoSkip: true,
            },
            grid: { color: palette.outline },
          },
        },
      },
    })
    return () => instance.destroy()
  }, [chart, hasValues])
  return (
    <section className="report-panel report-chart" aria-labelledby={`chart-${chart.id}`}>
      <h2 id={`chart-${chart.id}`} className="!text-base !mb-4">
        {chart.title}
      </h2>
      {hasValues ? (
        <div className="h-64 sm:h-72">
          <canvas
            ref={canvas}
            role="img"
            aria-label={`${chart.title}. Valores exactos en la tabla de datos siguiente.`}
          />
        </div>
      ) : (
        <p className="report-muted py-lg">
          Sin datos evaluables para este gráfico.
        </p>
      )}
      <p className="report-caption">{chart.note}</p>
      {hasValues && (
        <details className="text-sm">
          <summary className="cursor-pointer py-xs">
            Ver valores del gráfico
          </summary>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <caption className="sr-only">
                {chart.title}, en {chart.unit}
              </caption>
              <thead>
                <tr>
                  <th className="p-xs">Referencia</th>
                  {chart.series.map((s) => (
                    <th className="p-xs" key={s.label}>
                      {s.label} ({chart.unit})
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chart.labels.map((label, i) => (
                  <tr key={i} className="border-t border-outline">
                    <th className="p-xs font-normal">{label}</th>
                    {chart.series.map((s) => (
                      <td className="p-xs tabular-nums" key={s.label}>
                        {s.values[i] === null
                          ? 'Sin dato'
                          : s.values[i]?.toLocaleString('es-AR', {
                              maximumFractionDigits: 1,
                            })}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  )
}
